import {
  FluidElementType,
  FluidLocationStrategy,
  FluidMenuItem,
  isNullOrUndefined,
} from '@lmig/fluid-core';

import { IN_PAGE_NAV_INVALID_TARGET } from '../../../../constants/constants';
import { Utils } from '../../../../utils/utils';
import { hasSubmenu } from '../../../fluid-tree-menu/utils/fluid-tree-menu.utils';

// ================================================================ //
// -- In-Page Nav Utils

/**
 * Converts "menuItems" Prop into the format needed for "jump links"
 * in In-Page Nav's Tree Menu. The returned array of menu items is
 * configured so that Tree Menu will render anchor elements with hash
 * links that relate to "scroll targets" on the page. However, each
 * menu item uses the "route" property (instead of "href") so that
 * Tree Menu calls "preventDefault()" on the <a> elements when clicked -
 * this allows In-Page Nav to handle the click event & scroll to the
 * target element programmatically.
 * @param items - array of FluidMenuItem objects to be parsed
 * @param activeScrollTargetId - the ID of the currently active scroll target
 */
export const parseMenuItems = (
  items: FluidMenuItem[] = [],
  activeScrollTargetId: string
): FluidMenuItem[] => {
  return (
    items
      // Filter out items that don't have "label" or href/route properties
      .filter((item) => !!item.label && (item.href || item.route))
      .map((menuItem) => {
        // Extract "href" & "route" so the source config doesn't affect the output
        const { href, route, ...item } = menuItem;
        // Prefer "href" over "route"
        let targetId = href ?? route;
        // Remove leading "#" from "href" or "route" (if it exists)
        targetId.startsWith('#') && (targetId = targetId.slice(1));

        return {
          ...item,
          // Set "id" attr on the rendered menu item element
          id: `nav-${targetId}`,
          // Use "data" to store a "clean" string of the scroll target's ID
          data: targetId,
          // Set "route" so that Tree Menu calls "preventDefault()" on click
          route: `#${targetId}`,
          // Specify "anchor" so that Tree Menu doesn't render <button> elements
          elementType: FluidElementType.ANCHOR,
          selected: activeScrollTargetId === targetId,
          ...(hasSubmenu(item) && {
            submenu: parseMenuItems(item.submenu, activeScrollTargetId),
          }),
        };
      })
  );
};

/**
 * Returns a flattened array of references to all valid "scroll target"
 * elements specified in the "menuItems" prop.
 * @param host - the In-Page Nav "host" element
 * @param menuItems - FluidMenuItem array containing "id" attrs of "scroll targets"
 */
export const getScrollTargets = (
  host: HTMLFluidInPageNavElement,
  menuItems: FluidMenuItem[]
): HTMLElement[] => {
  if (isNullOrUndefined(menuItems) || !menuItems.length) return [];

  return menuItems.reduce((targetEls: HTMLElement[], item: FluidMenuItem) => {
    const selector = item.data;
    const scrollTarget = queryScrollTarget(host.pageRoot, selector);

    !!selector &&
      isNullOrUndefined(scrollTarget) &&
      Utils.consoleWarn(host, IN_PAGE_NAV_INVALID_TARGET(selector));
    return [
      ...targetEls,
      scrollTarget,
      ...getScrollTargets(host, item.submenu),
    ];
  }, []);
};

/**
 * Queries the provided "page root" for the "scroll target" element
 * with the given ID. Returns the element if found, or undefined if not.
 * @param pageRoot - the root element to search within
 * @param targetId - the ID of the target element to find
 */
export const queryScrollTarget = (
  pageRoot: HTMLElement | Document,
  targetId: string
): HTMLElement => {
  if (!targetId) return;
  return pageRoot?.querySelector(`#${targetId}`) as HTMLElement;
};

/**
 * Reads the current "window.location" URL & returns the ID of the
 * scroll target element specified in the URL (if one exists) based
 * on the provided "locationStrategy". If the URL contains a "scroll
 * target" ID, verifies that the target exists in the provided "pageRoot".
 * @param pageRoot - the root element to search within
 * @param locationStrategy - the strategy used to determine the scroll target
 * @param queryStringKey - query string key to find in the URL (if using QUERY strategy)
 */
export const getScrollTargetIdFromURL = (
  pageRoot: HTMLElement | Document,
  locationStrategy: FluidLocationStrategy,
  queryStringKey: string
): string => {
  const url = new URL(window.location.href);
  const targetId =
    locationStrategy === FluidLocationStrategy.HASH
      ? url.hash.slice(1)
      : url.searchParams.get(queryStringKey);
  return queryScrollTarget(pageRoot, targetId) ? targetId : undefined;
};
